import { useState, useEffect } from 'react';
import { useAuthStore, apiClient, WidgetSettings, FontStyle } from '../store/auth.store';
import toast from 'react-hot-toast';
import MediaLibrary from '../components/MediaLibrary';
import FontEditorModal from '../components/FontEditorModal';

type TabKey = 'general' | 'view' | 'image' | 'sound' | 'text';

const defaultFont: FontStyle = {
  fontFamily: 'Roboto Condensed',
  fontSize: 24,
  color: '#ff8c00',
  bold: true,
  italic: false,
  underline: false,
  textTransform: 'none',
  letterSpacing: 0,
  wordSpacing: 0,
  textAlign: 'center',
  verticalAlign: 'center',
  textShadow: true,
  shadowColor: '#000000',
  shadowBlur: 4,
  shadowOffsetX: 2,
  shadowOffsetY: 2,
  outline: false,
  outlineColor: '#000000',
  outlineWidth: 2,
};

const defaultSettings: WidgetSettings = {
  layout: 'image-top',
  bgColor: '#1a1a2e',
  bgOpacity: 0.9,
  borderRadius: 12,
  padding: 16,
  maxWidth: 400,
  defaultImage: '',
  imageSize: 64,
  imageBorderRadius: 50,
  animationIn: 'fade',
  animationOut: 'fade',
  animationDuration: 500,
  displayDuration: 5000,
  soundEnabled: true,
  soundUrl: '',
  minAmount: 0,
  maxAmount: 999999,
  headerTemplate: '{username} - {amount}!',
  messageTemplate: '{message}',
  headerFont: { ...defaultFont, fontSize: 24 },
  messageFont: { ...defaultFont, fontSize: 16, color: '#cccccc' },
  position: 'bottom-right',
};

export default function Settings() {
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const [settings, setSettings] = useState<WidgetSettings>(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [fontEditorOpen, setFontEditorOpen] = useState(false);
  const [editingFont, setEditingFont] = useState<'header' | 'message'>('header');

  useEffect(() => {
    if (user?.widgetSettings) {
      setSettings({
        ...defaultSettings,
        ...user.widgetSettings,
        headerFont: { ...defaultFont, ...user.widgetSettings.headerFont },
        messageFont: { ...defaultFont, ...user.widgetSettings.messageFont },
      });
    }
  }, [user]);

  const handleChange = (key: keyof WidgetSettings, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleFontChange = (target: 'headerFont' | 'messageFont', font: FontStyle) => {
    setSettings((prev) => ({ ...prev, [target]: font }));
  };

  const openFontEditor = (type: 'header' | 'message') => {
    setEditingFont(type);
    setFontEditorOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put('/users/settings', { widgetSettings: settings });
      updateUser(data.user);
      toast.success('Settings saved!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...defaultSettings, headerFont: { ...defaultFont }, messageFont: { ...defaultFont, fontSize: 16, color: '#cccccc' } });
    toast.success('Reset to defaults');
  };

  const tabs: { key: TabKey; icon: string; label: string }[] = [
    { key: 'general', icon: '⚙️', label: 'General' },
    { key: 'view', icon: '🎨', label: 'View' },
    { key: 'image', icon: '🖼️', label: 'Image' },
    { key: 'sound', icon: '🔊', label: 'Sound' },
    { key: 'text', icon: 'T', label: 'Text' },
  ];

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Widget Settings</h1>
        <div className="settings-actions">
          <button onClick={handleReset} className="btn-secondary">Reset</button>
          <button onClick={handleSave} className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : '✓ Save'}
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="tab-bar">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'general' && (
          <div className="settings-section">
            <h2>Position</h2>
            <div className="form-group">
              <label>Widget Position</label>
              <div className="position-grid">
                {([
                  'top-left', 'top-center', 'top-right',
                  'bottom-left', 'bottom-center', 'bottom-right',
                  'center',
                ] as const).map((pos) => (
                  <button
                    key={pos}
                    className={`position-btn ${settings.position === pos ? 'active' : ''}`}
                    onClick={() => handleChange('position', pos)}
                  >
                    {pos.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            <h2 style={{ marginTop: 30 }}>Filters</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Min Amount</label>
                <input type="number" value={settings.minAmount} onChange={(e) => handleChange('minAmount', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Max Amount</label>
                <input type="number" value={settings.maxAmount} onChange={(e) => handleChange('maxAmount', Number(e.target.value))} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'view' && (
          <div className="settings-section">
            <h2>Background</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Color</label>
                <div className="color-input">
                  <input type="color" value={settings.bgColor} onChange={(e) => handleChange('bgColor', e.target.value)} />
                  <input type="text" value={settings.bgColor} onChange={(e) => handleChange('bgColor', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Opacity ({settings.bgOpacity})</label>
                <input type="range" min="0" max="1" step="0.05" value={settings.bgOpacity} onChange={(e) => handleChange('bgOpacity', Number(e.target.value))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Border Radius ({settings.borderRadius}px)</label>
                <input type="range" min="0" max="50" value={settings.borderRadius} onChange={(e) => handleChange('borderRadius', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Padding ({settings.padding}px)</label>
                <input type="range" min="0" max="40" value={settings.padding} onChange={(e) => handleChange('padding', Number(e.target.value))} />
              </div>
            </div>
            <div className="form-group">
              <label>Max Width ({settings.maxWidth}px)</label>
              <input type="range" min="200" max="800" step="10" value={settings.maxWidth} onChange={(e) => handleChange('maxWidth', Number(e.target.value))} />
            </div>

            <h2 style={{ marginTop: 30 }}>Animations</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Animation In</label>
                <select value={settings.animationIn} onChange={(e) => handleChange('animationIn', e.target.value)}>
                  <option value="fade">Fade</option>
                  <option value="slide">Slide</option>
                  <option value="bounce">Bounce</option>
                  <option value="zoom">Zoom</option>
                  <option value="flip">Flip</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div className="form-group">
                <label>Animation Out</label>
                <select value={settings.animationOut} onChange={(e) => handleChange('animationOut', e.target.value)}>
                  <option value="fade">Fade</option>
                  <option value="slide">Slide</option>
                  <option value="bounce">Bounce</option>
                  <option value="zoom">Zoom</option>
                  <option value="flip">Flip</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Duration ({settings.animationDuration}ms)</label>
                <input type="range" min="100" max="2000" step="100" value={settings.animationDuration} onChange={(e) => handleChange('animationDuration', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Display ({settings.displayDuration / 1000}s)</label>
                <input type="range" min="1" max="30" step="0.5" value={settings.displayDuration / 1000} onChange={(e) => handleChange('displayDuration', Number(e.target.value) * 1000)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'image' && (
          <div className="settings-section">
            <h2>Layout</h2>
            <div className="layout-grid">
              {([
                { value: 'image-top', label: 'Image top, text bottom', icon: '⬆️' },
                { value: 'image-bottom', label: 'Image bottom, text top', icon: '⬇️' },
                { value: 'image-left', label: 'Image left, text right', icon: '⬅️' },
                { value: 'image-right', label: 'Image right, text left', icon: '➡️' },
                { value: 'image-background', label: 'Image as background', icon: '🖼️' },
              ] as const).map((layout) => (
                <button
                  key={layout.value}
                  className={`layout-btn ${settings.layout === layout.value ? 'active' : ''}`}
                  onClick={() => handleChange('layout', layout.value)}
                >
                  <span className="layout-icon">{layout.icon}</span>
                  <span className="layout-label">{layout.label}</span>
                </button>
              ))}
            </div>

            <h2 style={{ marginTop: 30 }}>Image Settings</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Size ({settings.imageSize}px)</label>
                <input type="range" min="24" max="200" value={settings.imageSize} onChange={(e) => handleChange('imageSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Border Radius ({settings.imageBorderRadius}%)</label>
                <input type="range" min="0" max="50" value={settings.imageBorderRadius} onChange={(e) => handleChange('imageBorderRadius', Number(e.target.value))} />
              </div>
            </div>

            <h2>Default Image</h2>
            <p className="helper-text" style={{ marginBottom: 12 }}>Select an image to show when user has no avatar</p>
            
            <MediaLibrary
              mediaType="image"
              onSelectUrl={(url) => {
                console.log('Image selected:', url);
                handleChange('defaultImage', url);
                toast.success('Image applied!');
              }}
            />
          </div>
        )}

        {activeTab === 'sound' && (
          <div className="settings-section">
            <div className="form-group">
              <label className="toggle-label">
                <input type="checkbox" checked={settings.soundEnabled} onChange={(e) => handleChange('soundEnabled', e.target.checked)} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                <span>Enable Sound</span>
              </label>
            </div>
            {settings.soundEnabled && (
              <MediaLibrary
                mediaType="sound"
                onSelectUrl={(url) => handleChange('soundUrl', url)}
              />
            )}
          </div>
        )}

        {activeTab === 'text' && (
          <div className="settings-section">
            <div className="form-group">
              <label>Header Template</label>
              <input
                type="text"
                value={settings.headerTemplate}
                onChange={(e) => handleChange('headerTemplate', e.target.value)}
                placeholder="{username} - {amount}!"
              />
              <p className="helper-text">Available: {'{username}'}, {'{amount}'}, {'{message}'}</p>
            </div>
            <div className="form-group">
              <label>Message Template</label>
              <input
                type="text"
                value={settings.messageTemplate}
                onChange={(e) => handleChange('messageTemplate', e.target.value)}
                placeholder="{message}"
              />
            </div>

            <div className="font-style-section">
              <div className="font-style-row">
                <label>Header Style:</label>
                <button className="btn-font" onClick={() => openFontEditor('header')}>
                  <span className="tt-preview" style={{ color: settings.headerFont.color, fontWeight: settings.headerFont.bold ? 'bold' : 'normal', fontStyle: settings.headerFont.italic ? 'italic' : 'normal', fontSize: 20 }}>Tt</span>
                  Style Header Font
                </button>
              </div>
              <div className="font-style-row">
                <label>Message Style:</label>
                <button className="btn-font" onClick={() => openFontEditor('message')}>
                  <span className="tt-preview" style={{ color: settings.messageFont.color, fontWeight: settings.messageFont.bold ? 'bold' : 'normal', fontStyle: settings.messageFont.italic ? 'italic' : 'normal', fontSize: 20 }}>Tt</span>
                  Style Message Font
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <FontEditorModal
        isOpen={fontEditorOpen}
        onClose={() => setFontEditorOpen(false)}
        font={editingFont === 'header' ? settings.headerFont : settings.messageFont}
        onSave={(font) => {
          handleFontChange(editingFont === 'header' ? 'headerFont' : 'messageFont', font);
          setFontEditorOpen(false);
        }}
      />

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .settings-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  .settings-header h1 {
    font-size: 24px;
  }
  .settings-actions {
    display: flex;
    gap: 12px;
  }
  .btn-save {
    padding: 10px 24px;
    background: #f59e0b;
    border: none;
    border-radius: 20px;
    color: white;
    font-weight: 600;
    font-size: 14px;
  }
  .btn-save:hover:not(:disabled) { background: #d97706; }
  .btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
  .btn-secondary {
    padding: 10px 20px;
    background: transparent;
    border: 1px solid #3f3f46;
    border-radius: 20px;
    color: #a1a1aa;
    font-size: 14px;
  }
  .btn-secondary:hover { border-color: #71717a; color: #e4e4e7; }
  
  .tab-bar {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #27272a;
    margin-bottom: 24px;
    overflow-x: auto;
  }
  .tab-btn {
    padding: 12px 20px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: #71717a;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s;
  }
  .tab-btn:hover { color: #e4e4e7; }
  .tab-btn.active { color: #f59e0b; border-bottom-color: #f59e0b; }
  .tab-icon { font-size: 16px; }
  
  .tab-content {
    max-width: 800px;
  }
  .settings-section h2 {
    font-size: 16px;
    color: #a1a1aa;
    margin-bottom: 16px;
    font-weight: 500;
  }
  .form-group { margin-bottom: 20px; }
  .form-group label {
    display: block;
    font-size: 13px;
    color: #71717a;
    margin-bottom: 8px;
  }
  .form-group input[type="text"],
  .form-group input[type="number"],
  .form-group select {
    width: 100%;
    padding: 10px 14px;
    background: #18181b;
    border: 1px solid #27272a;
    border-radius: 8px;
    color: #e4e4e7;
    font-size: 14px;
    outline: none;
  }
  .form-group input:focus, .form-group select:focus { border-color: #f59e0b; }
  .form-group input[type="range"] { width: 100%; accent-color: #f59e0b; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .helper-text { font-size: 12px; color: #52525b; margin-top: 6px; }
  
  .color-input { display: flex; gap: 10px; align-items: center; }
  .color-input input[type="color"] { width: 50px; height: 40px; border: none; border-radius: 8px; cursor: pointer; background: none; }
  .color-input input[type="text"] { flex: 1; }
  
  .position-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .position-btn {
    padding: 12px;
    background: #18181b;
    border: 1px solid #27272a;
    border-radius: 8px;
    color: #a1a1aa;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .position-btn:hover { border-color: #52525b; }
  .position-btn.active { border-color: #f59e0b; color: #f59e0b; background: rgba(245, 158, 11, 0.1); }
  
  .layout-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
  .layout-btn {
    padding: 20px 16px;
    background: #18181b;
    border: 1px solid #27272a;
    border-radius: 8px;
    color: #a1a1aa;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
  }
  .layout-btn:hover { border-color: #52525b; }
  .layout-btn.active { border-color: #f59e0b; background: rgba(245, 158, 11, 0.1); color: #f59e0b; }
  .layout-icon { font-size: 24px; }
  .layout-label { font-size: 12px; text-align: center; }
  
  .toggle-label { display: flex; align-items: center; gap: 12px; cursor: pointer; }
  .toggle-label input { display: none; }
  .toggle-track {
    width: 44px; height: 24px;
    background: #3f3f46;
    border-radius: 12px;
    position: relative;
    transition: background 0.2s;
  }
  .toggle-thumb {
    width: 20px; height: 20px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 2px; left: 2px;
    transition: transform 0.2s;
  }
  .toggle-label input:checked + .toggle-track { background: #f59e0b; }
  .toggle-label input:checked + .toggle-track .toggle-thumb { transform: translateX(20px); }
  
  .sound-input { display: flex; gap: 10px; }
  .sound-input input { flex: 1; }
  
  .font-style-section { margin-top: 24px; }
  .font-style-row { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }
  .font-style-row label { color: #71717a; font-size: 14px; white-space: nowrap; }
  .btn-font {
    padding: 8px 16px;
    background: transparent;
    border: none;
    color: #f59e0b;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px dashed #f59e0b;
  }
  .tt-preview { font-size: 18px; }
  
  @media (max-width: 768px) {
    .form-row { grid-template-columns: 1fr; }
    .position-grid { grid-template-columns: repeat(2, 1fr); }
  }
`;
